import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, formatDistanceToNowStrict, isPast } from 'date-fns';
import EntityCard from '../common/EntityCard';
import StatusChip from '../common/StatusChip';

/*
  GoalCard: one goal as a grid card in the same EntityCard shell the
  Categories and Accounts cards use — identity row (account color dot, name,
  outlook chip, kebab menu), the live saved/counted amount as the headline
  stat with a percent pill and the target beside it, the progress bar with
  the on-track detail in the chart slot, and a remaining / time-left footer.
  The optional goal image renders as a full-bleed banner above the card body.
*/

const formatCurrency = (amount, decimals = 0) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount || 0);

// Compact outlook badge for the identity row; the full projected/short-by
// wording lives under the progress bar.
const outlookChip = (outlook) => {
  if (!outlook) return { status: 'neutral', label: 'No projection' };
  if (outlook.daysUntil <= 0) {
    return outlook.onTrack
      ? { status: 'success', label: 'Reached' }
      : { status: 'error', label: 'Missed' };
  }
  return outlook.onTrack
    ? { status: 'success', label: 'On track' }
    : { status: 'warning', label: 'Short' };
};

const GoalCard = ({
  goal,
  account,
  outlook,
  onEdit,
  onDelete,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  const pattern = goal.trackingType === 'transactions';
  const current = outlook ? outlook.current : account ? Number(account.balance) || 0 : 0;
  const pct =
    goal.targetAmount > 0 ? Math.min(100, Math.max(0, (current / goal.targetAmount) * 100)) : 0;
  const color = (!pattern && account?.color) || '#1565c0';
  const targetDate = new Date(goal.targetDate);
  const savedWord = pattern ? 'Counted' : 'Saved';

  const meta = pattern
    ? `Transaction pattern${
        outlook && Number.isFinite(outlook.matchedCount) ? ` (${outlook.matchedCount} matched)` : ''
      }`
    : account
    ? account.name
    : 'Account missing';

  const detail = [];
  if (outlook) {
    if (outlook.daysUntil <= 0) {
      detail.push(
        outlook.onTrack ? 'Target reached.' : `Missed by ${formatCurrency(outlook.shortBy)}.`
      );
    } else if (outlook.onTrack) {
      detail.push(`Projected ${formatCurrency(outlook.projected)} by the target date.`);
    } else {
      detail.push(
        `Short by ${formatCurrency(outlook.shortBy)}, projected ${formatCurrency(outlook.projected)}.`
      );
      if (outlook.suggestion) {
        detail.push(
          `Add ${formatCurrency(outlook.suggestion.amount)}/${outlook.suggestion.per}` +
            (outlook.suggestion.actionName
              ? ` to "${outlook.suggestion.actionName}" to make it.`
              : ' to make it.')
        );
      }
    }
    if (outlook.truncated) {
      detail.push('Projected over the next 2 years; the target is further out.');
    }
  } else {
    detail.push('No projection because the account is missing.');
  }
  if (current <= 0) {
    detail.unshift(
      `Nothing ${savedWord.toLowerCase()} yet${
        current < 0 ? `, the ${pattern ? 'net is' : 'balance is'} ${formatCurrency(current)}` : ''
      }.`
    );
  }

  const chip = outlookChip(outlook);
  const remaining = Math.max(0, (Number(goal.targetAmount) || 0) - current);
  const timeLeft = isPast(targetDate)
    ? 'Past due'
    : `${formatDistanceToNowStrict(targetDate)} left`;

  return (
    <EntityCard
      canDrag={false}
      banner={goal.imageUrl || null}
      color={color}
      name={goal.name}
      meta={meta}
      chips={
        <StatusChip
          status={chip.status}
          label={chip.label}
          sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }}
        />
      }
      menuItems={[
        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(goal) },
        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => onDelete(goal) },
      ]}
      statLabel={`${savedWord} · by ${format(targetDate, 'd MMM yyyy')}`}
      statValue={formatCurrency(current)}
      delta={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <StatusChip
            status="neutral"
            label={`${Math.round(pct)}%`}
            sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }}
          />
          <Typography variant="caption" color="text.secondary" noWrap>
            of {formatCurrency(goal.targetAmount)}
          </Typography>
        </Box>
      }
      chart={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: pct > 0 ? alpha(color, 0.15) : 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: color },
            }}
          />
          {detail.map((line) => (
            <Typography key={line} variant="caption" color="text.secondary">
              {line}
            </Typography>
          ))}
          {goal.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {goal.notes}
            </Typography>
          )}
        </Box>
      }
      footer={
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            pb: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" noWrap>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatCurrency(remaining)}
            </Box>
            {' remaining'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {timeLeft}
          </Typography>
        </Box>
      }
      selectable={selectable}
      selected={selected}
      onToggleSelect={() => onToggleSelect && onToggleSelect(goal.id)}
    />
  );
};

export default GoalCard;
